import { createContext, useCallback, useContext, useState } from 'react';
import { api, ApiError } from './api';

export interface AuthUser {
  id: string;
  email: string;
  displayName: string | null;
  role: string;
}

interface AuthState {
  user: AuthUser | null;
  checking: boolean;
  login: (email: string, password: string) => Promise<void>;
  magicLogin: (token: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [checking, setChecking] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await api.auth.me();
      setUser(res.user);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) setUser(null);
      else setUser(null);
    } finally {
      setChecking(false);
    }
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const res = await api.auth.login(email, password);
    setUser(res.user);
  }, []);

  const magicLogin = useCallback(async (token: string) => {
    const res = await api.auth.magicLogin(token);
    setUser(res.user);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.auth.logout();
    } finally {
      setUser(null);
    }
  }, []);

  return <AuthContext.Provider value={{ user, checking, login, magicLogin, logout, refresh }}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
